"use client"

import ImageModal from "@/(admin)/components/image/select-image-button"
import { t } from "@/languages/i18n"
import CKEditorWrapper from "@/lib/config/lib/CkeditorWrapper"
import { MediaData } from "@/lib/config/types"
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Card, Col, Collapse, Form, Input, InputNumber, Row, Select, Space } from "antd"
import { FormInstance } from "antd/lib"
import React, { useState } from "react"
import { CampaignsSelect } from "./CampaignsSelect"

const { Panel } = Collapse

/** Cầu nối value/onChange của Form.Item sang CKEditor (controlled nên nhận được data set sau khi mount) */
const RichTextField: React.FC<{ value?: string; onChange?: (data: string) => void }> = ({ value = "", onChange }) => (
    <CKEditorWrapper value={value} onChange={data => onChange?.(data)} />
)

interface StoreFormFieldsProps {
    form: FormInstance
    featuredImageValue?: MediaData[]
    setFeaturedImageValue: (val: MediaData[]) => void
    galleryImagesValue?: MediaData[]
    setGalleryImagesValue: (val: MediaData[]) => void
    managerImages?: Record<number, MediaData[]>
    setManagerImages?: (index: number, val: MediaData[]) => void
}

const StoreFormFields: React.FC<StoreFormFieldsProps> = ({
    form,
    featuredImageValue,
    setFeaturedImageValue,
    galleryImagesValue,
    setGalleryImagesValue,
    managerImages = {},
    setManagerImages = () => { },
}) => {
    const { TextArea } = Input

    const [activeManagerKeys, setActiveManagerKeys] = useState<string[]>([])
    const getMediaFromManager = (managerIndex: number): MediaData[] => {
        return managerImages[managerIndex] || []
    }

    return (
        <Space direction="vertical" size="large" className="w-full">
            <Card title={t("Basic Information")} size="small">
                <Form.Item name="name" label={<span className="font-medium">{t("Tên cửa hàng")}</span>}>
                    <Input placeholder={t("Nhập tên cửa hàng...")} />
                </Form.Item>

                <Form.Item name="address" label={<span className="font-medium">{t("Address")}</span>}>
                    <TextArea rows={2} placeholder={t("Nhập địa chỉ...")} />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="longitude" label={<span className="font-medium">{t("Longitude")}</span>}>
                            <InputNumber style={{ width: "100%" }} placeholder="16.650329" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="latitude" label={<span className="font-medium">{t("Latitude")}</span>}>
                            <InputNumber style={{ width: "100%" }} placeholder="49.187755" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="googleMapCode" label={<span className="font-medium">{t("Mã chia sẻ Google Map")}</span>}>
                            <Input placeholder="5MQ2+34 Brno" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="phone"
                            label={<span className="font-medium">{t("Số điện thoại")}</span>}
                            rules={[{
                                pattern: /^[0-9+ ]+$/,
                                message: t("Số điện thoại chỉ bao gồm số, dấu + và khoảng trắng")
                            }]}
                        >
                            <Input placeholder="+420 777 958 383" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="email" label={<span className="font-medium">{t("Email")}</span>}>
                            <Input placeholder="info.brno@tamda.eu" />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            <Card title={t("Media")} size="small">
                <Form.Item label={<span className="font-medium">{t("Ảnh chính cửa hàng")}</span>} name="image">
                    <ImageModal
                        selectedMedia={featuredImageValue}
                        setSelectedMedia={media => {
                            setFeaturedImageValue(media)
                            form.setFieldValue("image", media.length > 0 ? media[0].id : null)
                        }}
                        selectMode="single"
                    />
                </Form.Item>

                <Form.Item label={<span className="font-medium">{t("Bộ sưu tập")}</span>} name="galeryPhotos">
                    <ImageModal
                        selectedMedia={galleryImagesValue}
                        setSelectedMedia={media => {
                            setGalleryImagesValue(media)
                            form.setFieldValue(
                                "galeryPhotos",
                                media.map(m => m.id),
                            )
                        }}
                        selectMode="multi"
                    />
                </Form.Item>
            </Card>

            <Card title={t("Quản lý")} size="small">
                <Form.List name="managers">
                    {(fields, { add, remove }) => (
                        <div className="flex flex-col gap-4">
                            <Collapse
                                activeKey={activeManagerKeys}
                                onChange={keys => setActiveManagerKeys(keys as string[])}
                                items={fields.map(({ key, name, ...restField }, index) => ({
                                    key: String(index),
                                    label: (
                                        <div className="flex justify-between items-center w-full pr-4">
                                            <span className="font-medium text-blue-600">
                                                {form.getFieldValue(["managers", name, "name"]) || `Quản lý ${index + 1}`}
                                            </span>
                                            <Space onClick={e => e.stopPropagation()}>
                                                <DeleteOutlined className="text-gray-400 hover:text-red-500" onClick={() => remove(name)} />
                                            </Space>
                                        </div>
                                    ),
                                    children: (
                                        <div className="flex flex-col gap-4 p-2">
                                            <Form.Item
                                                {...restField}
                                                name={[name, "name"]}
                                                label={<span className="font-medium text-xs text-gray-500 uppercase">{t("Name")}</span>}
                                                className="mb-0"
                                            >
                                                <Input placeholder={t("Tên quản lý")} />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, "image"]}
                                                label={<span className="font-medium text-xs text-gray-500 uppercase">{t("Image")}</span>}
                                                className="mb-0"
                                            >
                                                <ImageModal
                                                    selectedMedia={getMediaFromManager(name) as MediaData[]}
                                                    setSelectedMedia={media => {
                                                        const selectedId = media.length > 0 ? media[0].id : null
                                                        form.setFieldValue(["managers", name, "image"], selectedId)
                                                        if (setManagerImages) {
                                                            setManagerImages(name, media)
                                                        }
                                                    }}
                                                    selectMode="single"
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "url"]}
                                                label={<span className="font-medium text-xs text-gray-500 uppercase">{t("URL")}</span>}
                                                className="mb-0"
                                            >
                                                <Input placeholder="https://..." />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, "target"]}
                                                label={<span className="font-medium text-xs text-gray-500 uppercase">{t("Target")}</span>}
                                                initialValue="_self"
                                                className="mb-0"
                                            >
                                                <Select
                                                    options={[
                                                        { value: "_self", label: "_self" },
                                                        { value: "_blank", label: "_blank" },
                                                        { value: "_parent", label: "_parent" },
                                                        { value: "_top", label: "_top" },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </div>
                                    ),
                                }))}
                            />
                            <Button
                                type="dashed"
                                onClick={() => add()}
                                block
                                icon={<PlusOutlined />}
                                className="text-blue-600 border-blue-200 bg-blue-50"
                            >
                                {t("Thêm quản lý")}
                            </Button>
                        </div>
                    )}
                </Form.List>
            </Card>

            <Card title={t("Khung thời gian (Mở - Đóng)")} size="small">
                <Form.List name="timeRange">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Row key={key} gutter={16} align="middle" className="mb-2">
                                    <Col span={8}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'day']}
                                            className="mb-0"
                                        >
                                            <Input placeholder="thứ 2" />
                                        </Form.Item>
                                    </Col>

                                    <Col span={7}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'from']}
                                            className="mb-0"
                                            getValueProps={(value) => ({
                                                value: value ? value.substring(0, 5) : value,
                                            })}
                                        >
                                            <Input placeholder="08:00" />
                                        </Form.Item>
                                    </Col>

                                    {/* CỘT GIỜ KẾT THÚC */}
                                    <Col span={7}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'to']}
                                            className="mb-0"
                                            getValueProps={(value) => ({
                                                value: value ? value.substring(0, 5) : value,
                                            })}
                                        >
                                            <Input placeholder="20:00" />
                                        </Form.Item>
                                    </Col>

                                    <Col span={2}>
                                        <MinusCircleOutlined onClick={() => remove(name)} />
                                    </Col>
                                </Row>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    {t("Thêm khung thời gian")}
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Card>

            <Card title={t("Description")} size="small">
                <Form.Item name="description" className="mb-0">
                    <TextArea rows={4} placeholder={t("Input description...")} />
                </Form.Item>
            </Card>

            <Card title={t("Chỉ dẫn")} size="small">
                <Form.Item name="guidance" className="mb-0">
                    <RichTextField />
                </Form.Item>
            </Card>

            <Card title={t("Chiến dịch liên kết")} size="small">
                <Form.Item name="campaigns">
                    <CampaignsSelect />
                </Form.Item>
            </Card>
        </Space>
    )
}

export default StoreFormFields
